<template>
  <div class="player-metrics">
    <div class="header-row">
      <div class="header-left">
        <h2 class="mr-2">Device Metrics</h2>
        <span v-if="isLoading" class="inline-spinner" />
        <span v-else class="timestamp" :title="tooltipText">{{ lastUpdatedText }}</span>
      </div>
      <button
        type="button"
        class="metrics-refresh-btn"
        :disabled="isLoading"
        title="Refresh device metrics and proof of play"
        aria-label="Refresh device metrics and proof of play"
        @click="refreshAll"
      >
        <ReloadIcon />
      </button>
    </div>

    <div v-if="error" class="error-text">{{ error }}</div>

    <DeviceMetricsGrid v-if="!isLoading || metrics" :metrics="metrics" />

    <NowPlayingGrid :items="metrics?.currently_playing || []" />

    <ProofOfPlayTable ref="proofOfPlayTable" :deviceUuid="dashedUuid" />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import {
  getLatestMetricsByPlayerUuid,
  type MetricsData,
} from "@/helpers/api/metrics";
import { dashifyUuid } from "@/helpers/utils";
import DeviceMetricsGrid from "./DeviceMetricsGrid.vue";
import NowPlayingGrid from "./NowPlayingGrid.vue";
import ProofOfPlayTable from "./ProofOfPlayTable.vue";
import ReloadIcon from "@/components/icons/ReloadIcon.vue";

const props = defineProps<{
  deviceUuid?: string | null;
}>();

const metrics = ref<MetricsData | null>(null);
const isLoading = ref(false);
const error = ref("");
const lastUpdated = ref<Date | null>(null);
// Guard against out-of-order responses when the user switches between
// players quickly. Only the latest request is allowed to mutate state.
let latestRequestId = 0;

const dashedUuid = computed(() => dashifyUuid(props.deviceUuid));

const proofOfPlayTable = ref<InstanceType<typeof ProofOfPlayTable> | null>(null);

async function fetchDeviceMetrics() {
  const playerUuid = dashedUuid.value;
  if (!playerUuid) {
    error.value = "Device UUID not available";
    return;
  }
  const myId = ++latestRequestId;
  isLoading.value = true;
  error.value = "";
  try {
    const data = await getLatestMetricsByPlayerUuid(playerUuid, 1);
    if (myId !== latestRequestId) return; // stale, a newer fetch superseded us
    metrics.value = data && data.length > 0 ? data[0] : null;
    lastUpdated.value = metrics.value
      ? new Date(metrics.value.created_at)
      : null;
  } catch (e: any) {
    if (myId !== latestRequestId) return;
    error.value = e?.message || "Failed to load metrics";
  } finally {
    if (myId === latestRequestId) {
      isLoading.value = false;
    }
  }
}

// One button refreshes both panels. ProofOfPlayTable exposes refresh() so
// we don't have to duplicate its fetch params (timeRange, deviceUuid) here.
function refreshAll() {
  fetchDeviceMetrics();
  proofOfPlayTable.value?.refresh();
}

// Initial fetch + refetch on every deviceUuid change in one declaration.
watch(() => props.deviceUuid, fetchDeviceMetrics, { immediate: true });

const lastUpdatedText = computed(() => {
  if (!lastUpdated.value) return "";
  return `Updated ${lastUpdated.value.toLocaleTimeString()}`;
});

const tooltipText = computed(() =>
  lastUpdated.value ? lastUpdated.value.toLocaleString() : ""
);
</script>

<style scoped>
.player-metrics {
  margin-top: 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  min-width: 0;
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  width: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-width: 0;
}

.metrics-refresh-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 50%;
  background: #fff;
  color: #374151;
  cursor: pointer;
}

.metrics-refresh-btn:hover:not(:disabled) {
  background: #f9fafb;
  color: #111827;
}

.metrics-refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.timestamp {
  color: #6b7280;
  font-size: 0.8rem;
}

.inline-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-text {
  color: #b91c1c;
  margin: 0.5rem 0
}
</style>
